/**
 * promo-editor.ts — Kiểu + hàm thuần cho Platform → Mã ưu đãi.
 * Khớp backend `ecdp_backend_source/src/modules/billing/promo-service.ts`.
 */
import dayjs from 'dayjs'
import { formatIntInput, parseIntInput } from './pricing-editor'

export type PromoDiscountType = 'percent' | 'fixed'

export interface PromoCodeItem {
  id: string
  code: string
  description: string
  discountType: PromoDiscountType
  discountValue: number
  maxDiscountAmount: number | null
  planIds: string[]
  minDays: number | null
  minAmount: number | null
  startsAt: string | null
  endsAt: string | null
  maxRedemptions: number | null
  maxPerOrg: number
  firstPurchaseOnly: boolean
  stackableWithDurationDiscount: boolean
  isActive: boolean
  createdAt: string
  updatedAt: string
  usedCount: number
  totalDiscounted: number
}

export interface PromoRedemptionItem {
  id: string
  orgId: string
  orgName: string
  orderId: string
  paymentCode: string | null
  planId: string | null
  cycle: string | null
  amountPaid: number
  amountDiscounted: number
  createdAt: string
}

/** Body gửi POST/PUT. */
export interface PromoCodeInput {
  code: string
  description: string
  discountType: PromoDiscountType
  discountValue: number | null
  maxDiscountAmount: number | null
  planIds: string[]
  minDays: number | null
  minAmount: number | null
  startsAt: string | null
  endsAt: string | null
  maxRedemptions: number | null
  maxPerOrg: number | null
  firstPurchaseOnly: boolean
  stackableWithDurationDiscount: boolean
  isActive: boolean
}

/** Trạng thái form: số dạng chuỗi có dấu chấm nghìn, ngày dạng datetime-local. */
export interface PromoForm {
  code: string
  description: string
  discountType: PromoDiscountType
  discountValue: string
  maxDiscountAmount: string
  planIds: string[]
  minDays: string
  minAmount: string
  startsAt: string
  endsAt: string
  maxRedemptions: string
  maxPerOrg: string
  firstPurchaseOnly: boolean
  stackableWithDurationDiscount: boolean
  isActive: boolean
}

export const EMPTY_PROMO_FORM: PromoForm = {
  code: '', description: '', discountType: 'percent', discountValue: '', maxDiscountAmount: '', planIds: [],
  minDays: '', minAmount: '', startsAt: '', endsAt: '', maxRedemptions: '', maxPerOrg: '1',
  firstPurchaseOnly: false, stackableWithDurationDiscount: true, isActive: true,
}

const LOCAL_FMT = 'YYYY-MM-DDTHH:mm'

export function toLocalInput(iso: string | null): string {
  return iso ? dayjs(iso).format(LOCAL_FMT) : ''
}
export function fromLocalInput(s: string): string | null {
  if (!s) return null
  const d = dayjs(s)
  return d.isValid() ? d.toISOString() : null
}

export function promoToForm(p: PromoCodeItem): PromoForm {
  return {
    code: p.code,
    description: p.description,
    discountType: p.discountType,
    discountValue: p.discountType === 'percent' ? String(p.discountValue) : formatIntInput(p.discountValue),
    maxDiscountAmount: formatIntInput(p.maxDiscountAmount),
    planIds: p.planIds,
    minDays: p.minDays == null ? '' : String(p.minDays),
    minAmount: formatIntInput(p.minAmount),
    startsAt: toLocalInput(p.startsAt),
    endsAt: toLocalInput(p.endsAt),
    maxRedemptions: formatIntInput(p.maxRedemptions),
    maxPerOrg: String(p.maxPerOrg),
    firstPurchaseOnly: p.firstPurchaseOnly,
    stackableWithDurationDiscount: p.stackableWithDurationDiscount,
    isActive: p.isActive,
  }
}

export function formToInput(f: PromoForm): PromoCodeInput {
  const percent = f.discountType === 'percent'
  return {
    code: f.code.trim().toUpperCase(),
    description: f.description.trim(),
    discountType: f.discountType,
    discountValue: parseIntInput(f.discountValue),
    maxDiscountAmount: percent ? parseIntInput(f.maxDiscountAmount) : null,
    planIds: f.planIds,
    minDays: parseIntInput(f.minDays),
    minAmount: parseIntInput(f.minAmount),
    startsAt: fromLocalInput(f.startsAt),
    endsAt: fromLocalInput(f.endsAt),
    maxRedemptions: parseIntInput(f.maxRedemptions),
    maxPerOrg: parseIntInput(f.maxPerOrg),
    firstPurchaseOnly: f.firstPurchaseOnly,
    stackableWithDurationDiscount: f.stackableWithDurationDiscount,
    isActive: f.isActive,
  }
}

/** Kiểm tra nhanh phía client (server vẫn kiểm lại). null = hợp lệ. */
export function validatePromoForm(f: PromoForm): string | null {
  const code = f.code.trim().toUpperCase()
  if (!/^[A-Z0-9][A-Z0-9_-]{2,31}$/.test(code)) return 'Mã: 3–32 ký tự, chỉ gồm chữ không dấu, số, "-", "_"'
  const v = parseIntInput(f.discountValue)
  if (f.discountType === 'percent' && (v == null || v < 1 || v > 100)) return 'Phần trăm giảm phải từ 1 đến 100'
  if (f.discountType === 'fixed' && (v == null || v < 1000)) return 'Số tiền giảm tối thiểu 1.000đ'
  if (f.startsAt && f.endsAt && dayjs(f.endsAt).valueOf() <= dayjs(f.startsAt).valueOf()) return 'Thời gian kết thúc phải sau thời gian bắt đầu'
  return null
}

/** "20%" (tối đa 500.000đ) | "300.000đ". */
export function discountLabel(p: Pick<PromoCodeItem, 'discountType' | 'discountValue' | 'maxDiscountAmount'>): string {
  if (p.discountType === 'percent') {
    return `${p.discountValue}%${p.maxDiscountAmount != null ? ` (tối đa ${formatIntInput(p.maxDiscountAmount)}đ)` : ''}`
  }
  return `${formatIntInput(p.discountValue)}đ`
}

export type PromoStatus = 'active' | 'scheduled' | 'expired' | 'exhausted' | 'inactive'

export function promoStatus(p: PromoCodeItem, now: Date = new Date()): PromoStatus {
  if (!p.isActive) return 'inactive'
  if (p.endsAt && new Date(p.endsAt).getTime() <= now.getTime()) return 'expired'
  if (p.maxRedemptions != null && p.usedCount >= p.maxRedemptions) return 'exhausted'
  if (p.startsAt && new Date(p.startsAt).getTime() > now.getTime()) return 'scheduled'
  return 'active'
}

export const PROMO_STATUS_LABEL: Record<PromoStatus, { text: string; variant: 'success' | 'secondary' | 'warning' | 'destructive' | 'outline' }> = {
  active: { text: 'Đang áp dụng', variant: 'success' },
  scheduled: { text: 'Chưa bắt đầu', variant: 'warning' },
  expired: { text: 'Hết hạn', variant: 'secondary' },
  exhausted: { text: 'Hết lượt', variant: 'secondary' },
  inactive: { text: 'Đã tắt', variant: 'outline' },
}
